import { Country } from "shared";
import { useCallback, useState } from "react";
import { useEnvironment, useUrls } from "../../app/Providers/AppContext";
import { downloadDocumentUrl } from "../../utils/downloadDocumentUrl";

export type UseDownloadIdCardResult = {
  download: () => Promise<void>;
  isDownloading: boolean;
  error: Error | undefined;
};

export const useDownloadIdCard = (
  policyNumber: string,
  country: Country
): UseDownloadIdCardResult => {
  const urls = useUrls();
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<Error>();
  const env = useEnvironment();

  const download = useCallback(async () => {
    setIsDownloading(true);

    // Can't download locally so show an alert with the URL
    // Comment this if you want to see the error message
    if (env === "localhost") {
      alert(
        "Download id card: " +
          urls.insurance.getCardUrl(
            window.location.origin,
            country,
            policyNumber
          )
      );
      setIsDownloading(false);
      return;
    }

    const response = await fetch(
      urls.insurance.getCardUrl(window.location.origin, country, policyNumber),
      {
        headers: {
          "content-type": "application/json",
        },
      }
    );

    if (!response || !response.ok) {
      setError(new Error("Couldn't fetch id cards url"));
      setIsDownloading(false);
      return;
    }

    const url = await response.text();

    const documentName = `${policyNumber}_${country}_id_card.pdf`;
    const didDownload = await downloadDocumentUrl(url, documentName);

    setIsDownloading(false);
    setError(
      didDownload ? undefined : new Error("Unable to launch id cards url")
    );
  }, [policyNumber, country]);

  return { download, isDownloading, error };
};
